/**
 * AdminWebsiteRequestsPage — Admin/team overzicht alle website-aanvragen (FASE 4).
 *
 * Functies:
 *  - Lijst + filter op status + zoek op klant
 *  - Per aanvraag: status timeline, klant info, admin notes
 *  - Status updaten via modal (alleen admin)
 *  - Logo preview (als geupload)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Globe, RefreshCw, Search, Filter, Loader2, CheckCircle, X, Edit2,
  ChevronRight, Calendar, AlertCircle,
} from 'lucide-react';

const STATUS_LABELS = {
  aangevraagd: 'Aangevraagd', in_review: 'In review', in_uitvoering: 'In uitvoering',
  opgeleverd: 'Opgeleverd', gepubliceerd: 'Gepubliceerd', afgewezen: 'Afgewezen',
};
const STATUS_COLORS = {
  aangevraagd: 'text-blue-300 bg-blue-500/10',
  in_review: 'text-purple-300 bg-purple-500/10',
  in_uitvoering: 'text-[#D4A017] bg-[#D4A017]/10',
  opgeleverd: 'text-emerald-400 bg-emerald-500/10',
  gepubliceerd: 'text-cyan-400 bg-cyan-500/10',
  afgewezen: 'text-red-400 bg-red-500/10',
};

export default function AdminWebsiteRequestsPage() {
  const { api, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', `/api/admin/website-requests${statusFilter ? `?status=${statusFilter}` : ''}`);
      setRequests(r.data.requests || []);
    } catch {
      toast.error('Aanvragen laden mislukt');
    }
    setLoading(false);
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = requests.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.klant_email || '').toLowerCase().includes(q) ||
      (r.domein_wens || '').toLowerCase().includes(q) ||
      (r.bedrijfsnaam || '').toLowerCase().includes(q)
    );
  });

  const stats = {
    aangevraagd: requests.filter((r) => r.status === 'aangevraagd').length,
    in_uitvoering: requests.filter((r) => r.status === 'in_uitvoering').length,
    opgeleverd: requests.filter((r) => r.status === 'opgeleverd' || r.status === 'gepubliceerd').length,
    totaal: requests.length,
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[#D4A017] text-xs uppercase tracking-wider font-bold mb-1">Admin</p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
              Website-aanvragen
            </h1>
            <p className="text-white/60 text-sm mt-1">Beheer alle klant-aanvragen voor nieuwe websites, redesigns en landingspagina's</p>
          </div>
          <button onClick={load} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="admin-requests-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Aangevraagd', value: stats.aangevraagd, color: 'text-blue-300' },
            { label: 'In uitvoering', value: stats.in_uitvoering, color: 'text-[#D4A017]' },
            { label: 'Opgeleverd', value: stats.opgeleverd, color: 'text-emerald-400' },
            { label: 'Totaal', value: stats.totaal, color: 'text-white/70' },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/5" data-testid={`req-stat-${s.label.toLowerCase().replace(' ', '-')}`}>
              <p className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek op klant of domein" className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40" data-testid="admin-requests-search" />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-white/40" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="admin-requests-status-filter">
              <option value="">Alle</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-12 text-center text-white/40" data-testid="admin-requests-empty">Geen aanvragen.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => (
              <div key={r.id} className="bg-white/5 rounded-xl p-5 border border-white/10" data-testid={`admin-request-${r.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Globe size={14} className="text-[#D4A017]" />
                      <h3 className="font-bold text-white">{r.domein_wens}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[r.status]}`}>{STATUS_LABELS[r.status] || r.status}</span>
                    </div>
                    <p className="text-white/60 text-sm">
                      {r.bedrijfsnaam && <>{r.bedrijfsnaam} · </>}
                      <span className="text-white">{r.klant_name || r.klant_email}</span>
                      <span className="text-white/40 ml-2">({r.klant_email})</span>
                    </p>
                    <p className="text-white/40 text-xs mt-1">
                      Type: {r.type_website} · {r.aantal_paginas} pagina's · {r.branche || 'geen branche'} ·
                      Aangevraagd {new Date(r.created_at).toLocaleDateString('nl-NL')}
                    </p>
                  </div>
                  <button onClick={() => setEditing(r)} className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 flex-shrink-0" data-testid={`edit-request-${r.id}`}>
                    <Edit2 size={12} /> Bewerken
                  </button>
                </div>

                {r.logo_file_id && (
                  <div className="mb-2">
                    <p className="text-white/40 text-xs mb-1">Logo:</p>
                    <LogoPreview fileId={r.logo_file_id} />
                  </div>
                )}

                {r.extra_wensen && (
                  <details className="mt-2">
                    <summary className="text-white/60 text-xs cursor-pointer hover:text-white">Wensen tonen</summary>
                    <p className="text-white/80 text-xs mt-1 leading-relaxed whitespace-pre-line">{r.extra_wensen}</p>
                  </details>
                )}
                {r.admin_notes && (
                  <div className="mt-2 bg-amber-500/10 border-l-2 border-amber-500 px-3 py-2 rounded">
                    <p className="text-xs text-amber-300 font-bold mb-0.5">Admin notitie:</p>
                    <p className="text-xs text-white/80">{r.admin_notes}</p>
                  </div>
                )}
                {r.estimated_delivery && (
                  <p className="text-xs text-white/50 mt-2"><Calendar size={11} className="inline mr-1" /> Oplevering: {r.estimated_delivery}</p>
                )}
                {r.final_url && (
                  <p className="text-xs mt-2"><a href={r.final_url} target="_blank" rel="noopener noreferrer" className="text-[#D4A017] hover:underline">{r.final_url} →</a></p>
                )}
              </div>
            ))}
          </div>
        )}

        {editing && <EditModal api={api} req={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      </div>
    </div>
  );
}


function LogoPreview({ fileId }) {
  const { getToken } = useAuth();
  const token = getToken();
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  return (
    <img
      data-testid={`logo-preview-${fileId}`}
      src={`${backend}/api/files/${fileId}?auth=${encodeURIComponent(token)}`}
      alt="Logo"
      style={{ maxHeight: 60, maxWidth: 200, background: 'white', padding: 6, borderRadius: 6 }}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
}


function EditModal({ api, req, onClose, onSaved }) {
  const [status, setStatus] = useState(req.status);
  const [notes, setNotes] = useState(req.admin_notes || '');
  const [eta, setEta] = useState(req.estimated_delivery || '');
  const [finalUrl, setFinalUrl] = useState(req.final_url || '');
  const [afwijzing, setAfwijzing] = useState(req.afwijzing_reden || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api('patch', `/api/admin/website-requests/${req.id}`, {
        status, admin_notes: notes, estimated_delivery: eta, final_url: finalUrl,
        afwijzing_reden: status === 'afgewezen' ? afwijzing : undefined,
      });
      toast.success('Aanvraag bijgewerkt');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Opslaan mislukt');
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" data-testid="edit-request-modal">
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Aanvraag bewerken</h2>
          <button onClick={onClose}><X size={18} className="text-white/40 hover:text-white" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Status</span>
            <select data-testid="edit-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white">
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          {status === 'afgewezen' && (
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Reden afwijzing</span>
              <textarea value={afwijzing} onChange={(e) => setAfwijzing(e.target.value)} rows={2} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            </label>
          )}
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Verwachte oplevering</span>
            <input value={eta} onChange={(e) => setEta(e.target.value)} placeholder="bv. 'binnen 2 weken'" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Final URL (bij opgeleverd)</span>
            <input value={finalUrl} onChange={(e) => setFinalUrl(e.target.value)} placeholder="https://nieuwe-website.nl" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-xs" />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Admin notities (zichtbaar voor klant)</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
          </label>
          <button onClick={save} disabled={saving} data-testid="save-request" className="w-full bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 text-[#0f172a] font-bold py-2.5 rounded-xl flex items-center justify-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Opslaan
          </button>
        </div>
      </div>
    </div>
  );
}
